/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  AccountCreateReq,
  AccountCreateRes,
  AccountExportReq,
  AccountSignupReq,
  AccountSignupRes,
  BaseErrorResponse,
  ChangeSponsorReq,
  ChangeSponsorFrontlineReq,
  CheckExistsReq,
  CheckExistsRes,
  CreatePayoutParamsReq,
  EditPropertyReq,
  EmptySuccessResponse,
  ExportOperationParams,
  GetAccountCurrentRes,
  GetAccountInvoiceListRes,
  GetAccountListRes,
  GetAccountPayoutListRes,
  GetAccountPosOrderRes,
  GetAccountPosOrdersRes,
  GetAccountPropertiesHistoryRes,
  GetAccountPropertiesRes,
  GetAccountPropertyRes,
  GetAccountRes,
  GetDownlineRes,
  GetFavoriteAccountsRes,
  GetFrontlineRes,
  GetLastBinaryPositionRes,
  GetPeriodsRes,
  GetPositionRes,
  GetProfileValueRes,
  GetProfileValuesRes,
  GetTransactionLogRes,
  GetTransactionRes,
  GetTransactionsRes,
  GetUplineRes,
  GetWalletsListRes,
  InternalTransferReq,
  IsFieldConfirmedRes,
  LoginRes,
  PosOrderData,
  PostTransactionReq,
  PostTransferReq,
  SearchArrayRes,
  SearchRes,
  SendSupportMailReq,
  SetAccountPropertyRes,
  SetPasswordByIdReq,
  SetPasswordReq,
  SetProfileReq,
  SetProfileValueReq,
  UniquenessCheckReq,
  UniquenessCheckRes,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useAccount() {
  class Account<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Account
     * @name AccountControllerGetAccounts
     * @summary Returns a list of accounts
     * @request GET:/api3/account
     * @secure
     * @response `200` `GetAccountListRes`
     * @response `400` `BaseErrorResponse`
     */
    accountControllerGetAccounts = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountListRes, BaseErrorResponse>({
        path: '/api3/account',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountControllerStartExport
     * @summary Starts export operation
     * @request POST:/api3/account/export
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    accountControllerStartExport = (data: AccountExportReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/account/export',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountControllerGetAccount
     * @summary Returns account information
     * @request GET:/api3/account/{id}/info
     * @secure
     * @response `200` `GetAccountRes`
     * @response `400` `BaseErrorResponse`
     */
    accountControllerGetAccount = (id: number, params: RequestParams = {}) =>
      this.request<GetAccountRes, BaseErrorResponse>({
        path: `/api3/account/${id}/info`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountControllerCheckAccountExists
     * @summary Checking the existence of an account
     * @request POST:/api3/account/check-exists
     * @response `200` `CheckExistsRes`
     * @response `400` `BaseErrorResponse`
     */
    accountControllerCheckAccountExists = (data: CheckExistsReq, params: RequestParams = {}) =>
      this.request<CheckExistsRes, BaseErrorResponse>({
        path: '/api3/account/check-exists',
        method: 'POST',
        body: data,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountControllerDisable
     * @summary Deactivating account
     * @request POST:/api3/account/{id}/disable
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    accountControllerDisable = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/disable`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountControllerUndelete
     * @summary Undelete account
     * @request POST:/api3/account/{id}/undelete
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    accountControllerUndelete = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/undelete`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountControllerDelete
     * @summary Soft account deletion
     * @request DELETE:/api3/account/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    accountControllerDelete = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ConfirmationControllerIsConfirmed
     * @summary Checking a field for confirmation
     * @request GET:/api3/account/{id}/{alias}-confirm
     * @secure
     * @response `200` `IsFieldConfirmedRes`
     * @response `400` `BaseErrorResponse`
     */
    confirmationControllerIsConfirmed = (alias: string, id: number, params: RequestParams = {}) =>
      this.request<IsFieldConfirmedRes, BaseErrorResponse>({
        path: `/api3/account/${id}/${alias}-confirm`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ConfirmationControllerSetConfirmed
     * @summary Setting the field confirmation
     * @request PUT:/api3/account/{id}/{alias}-confirm
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    confirmationControllerSetConfirmed = (alias: string, id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/${alias}-confirm`,
        method: 'PUT',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ConfirmationControllerUnsetConfirmed
     * @summary Unset field confirmation
     * @request DELETE:/api3/account/{id}/{alias}-confirm
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    confirmationControllerUnsetConfirmed = (alias: string, id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/${alias}-confirm`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * @description Function to be used in the NRM and in the cabinet component if an already authorized user registers a new user below the network.
     *
     * @tags Account creation
     * @name AccountCreateControllerCreateAccount
     * @summary Account registration
     * @request POST:/api3/account/create
     * @secure
     * @response `200` `AccountCreateRes`
     * @response `400` `BaseErrorResponse`
     */
    accountCreateControllerCreateAccount = (data: AccountCreateReq, params: RequestParams = {}) =>
      this.request<AccountCreateRes, BaseErrorResponse>({
        path: '/api3/account/create',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountCurrentControllerGetAccountCurrent
     * @summary Retrieving data about the current network account
     * @request GET:/api3/account/current
     * @secure
     * @response `200` `GetAccountCurrentRes`
     * @response `400` `BaseErrorResponse`
     */
    accountCurrentControllerGetAccountCurrent = (params: RequestParams = {}) =>
      this.request<GetAccountCurrentRes, BaseErrorResponse>({
        path: '/api3/account/current',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name DownlineControllerGetDownline
     * @summary Getting the tree of children of the position
     * @request GET:/api3/account/{id}/downline
     * @secure
     * @response `200` `GetDownlineRes`
     * @response `400` `BaseErrorResponse`
     */
    downlineControllerGetDownline = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 0 */
        treeId?: any;
        /** @example null */
        positionId?: any;
        /**
         * @min 1
         * @example 1
         */
        offset?: any;
        /**
         * Sampling depth
         * @min 0
         * @example 3
         */
        depth?: any;
        /**
         * Show deleted or disabled accounts. Administrator only
         * @example false
         */
        showInactiveAccounts?: boolean;
        /**
         * Requested marketing properties
         * @example [{"0":"PV"},{"1":"AR"}]
         */
        properties?: object;
        /**
         * Sort options
         * @example [{"s.level":"ASC"}]
         */
        orderBy?: object;
        /**
         * Filtering parameters
         * @example [{"0":{"field":"p.email"}},{"0":{"value":"user3@example.com"}},{"0":{"operation":"="}},{"1":{"field":"p.firstname"}},{"1":{"value":"Lesa"}},{"1":{"operation":"="}}]
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetDownlineRes, BaseErrorResponse>({
        path: `/api3/account/${id}/downline`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account favorite
     * @name FavoriteAccountControllerGetFavoriteAccounts
     * @summary Get favorite accounts
     * @request GET:/api3/account/favorite
     * @secure
     * @response `200` `GetFavoriteAccountsRes`
     * @response `400` `BaseErrorResponse`
     */
    favoriteAccountControllerGetFavoriteAccounts = (params: RequestParams = {}) =>
      this.request<GetFavoriteAccountsRes, BaseErrorResponse>({
        path: '/api3/account/favorite',
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account favorite
     * @name FavoriteAccountControllerAddFavorite
     * @summary Add account to favorites
     * @request PUT:/api3/account/{id}/favorite
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    favoriteAccountControllerAddFavorite = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/favorite`,
        method: 'PUT',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account favorite
     * @name FavoriteAccountControllerRemoveFavorite
     * @summary Remove account from favorites
     * @request DELETE:/api3/account/{id}/favorite
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    favoriteAccountControllerRemoveFavorite = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/favorite`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name FrontLineControllerGetFrontline
     * @summary Getting the children of the position
     * @request GET:/api3/account/{id}/frontline
     * @secure
     * @response `200` `GetFrontlineRes`
     * @response `400` `BaseErrorResponse`
     */
    frontLineControllerGetFrontline = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /**
         * @min 0
         * @example 0
         */
        treeId?: any;
        /**
         * Show deleted or disabled accounts. Administrator only
         * @example false
         */
        showInactiveAccounts?: boolean;
        showDisabledAccounts?: boolean;
        /**
         * Requested marketing properties
         * @example [{"0":"AR"},{"1":"PV"}]
         */
        properties?: object;
        /**
         * Filtering parameters
         * @example [{"0":{"field":"p.email"}},{"0":{"value":"user3@example.com"}},{"0":{"operation":"="}},{"1":{"field":"p.firstname"}},{"1":{"value":"Lesa"}},{"1":{"operation":"="}}]
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetFrontlineRes, BaseErrorResponse>({
        path: `/api3/account/${id}/frontline`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account impersonate
     * @name AccountImpersonateControllerImpersonate
     * @summary Get account access tokens
     * @request POST:/api3/account/{id}/impersonate/login
     * @secure
     * @response `200` `LoginRes`
     * @response `400` `BaseErrorResponse`
     */
    accountImpersonateControllerImpersonate = (id: number, params: RequestParams = {}) =>
      this.request<LoginRes, BaseErrorResponse>({
        path: `/api3/account/${id}/impersonate/login`,
        method: 'POST',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Invoice
     * @name AccountInvoiceControllerGetAccountInvoiceList
     * @summary Get a list of account invoices
     * @request GET:/api3/account/{id}/invoice
     * @secure
     * @response `200` `GetAccountInvoiceListRes`
     * @response `400` `BaseErrorResponse`
     */
    accountInvoiceControllerGetAccountInvoiceList = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountInvoiceListRes, BaseErrorResponse>({
        path: `/api3/account/${id}/invoice`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name PasswordControllerSetPassword
     * @summary Setting a password
     * @request POST:/api3/account/set-password
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    passwordControllerSetPassword = (data: SetPasswordReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/account/set-password',
        method: 'POST',
        body: data,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name PasswordControllerSetPasswordById
     * @summary Setting a password
     * @request POST:/api3/account/{id}/set-password
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    passwordControllerSetPasswordById = (id: number, data: SetPasswordByIdReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/set-password`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payout
     * @name AccountPayoutControllerGetAccountInvoiceList
     * @summary Get a list of withdrawal requests
     * @request GET:/api3/account/{id}/payout
     * @secure
     * @response `200` `GetAccountPayoutListRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPayoutControllerGetAccountInvoiceList = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /**
         * Sort options
         * @example {"orderBy":{"payout.created_at":"ASC"}}
         */
        orderBy?: object;
        /**
         * Filtering parameters
         * @example {"filter":[{"field":"wallet-type.title","value":"Bonus","operation":"like"}]}
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountPayoutListRes, BaseErrorResponse>({
        path: `/api3/account/${id}/payout`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payout
     * @name AccountPayoutControllerCreateInvoice
     * @summary Create payout
     * @request POST:/api3/account/{id}/payout
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    accountPayoutControllerCreateInvoice = (id: number, data: CreatePayoutParamsReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/payout`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name AccountPeriodControllerGetAccountPeriods
     * @summary Getting a list of account's periods
     * @request GET:/api3/account/{id}/available-period
     * @secure
     * @response `200` `GetPeriodsRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPeriodControllerGetAccountPeriods = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 3 */
        positionId?: any;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetPeriodsRes, BaseErrorResponse>({
        path: `/api3/account/${id}/available-period`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account POS
     * @name AccountPosOrderControllerGetAccountOrders
     * @summary Getting a list of account orders
     * @request GET:/api3/account/{id}/pos/order
     * @secure
     * @response `200` `GetAccountPosOrdersRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPosOrderControllerGetAccountOrders = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountPosOrdersRes, BaseErrorResponse>({
        path: `/api3/account/${id}/pos/order`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account POS
     * @name AccountPosOrderControllerCreateOrder
     * @summary Creating an order
     * @request POST:/api3/account/{id}/pos/order
     * @secure
     * @response `200` `GetAccountPosOrderRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPosOrderControllerCreateOrder = (id: number, data: PosOrderData, params: RequestParams = {}) =>
      this.request<GetAccountPosOrderRes, BaseErrorResponse>({
        path: `/api3/account/${id}/pos/order`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account POS
     * @name AccountPosOrderControllerUpdateOrder
     * @summary Update order
     * @request PUT:/api3/account/{id}/pos/order
     * @secure
     * @response `200` `GetAccountPosOrderRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPosOrderControllerUpdateOrder = (id: number, data: PosOrderData, params: RequestParams = {}) =>
      this.request<GetAccountPosOrderRes, BaseErrorResponse>({
        path: `/api3/account/${id}/pos/order`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account position
     * @name AccountPositionControllerGetPosition
     * @summary Getting a list of account positions
     * @request GET:/api3/account/{id}/position
     * @secure
     * @response `200` `GetPositionRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPositionControllerGetPosition = (
      id: number,
      query?: {
        /** @example 1 */
        treeId?: number;
        /** @example ["PV","CV"] */
        properties?: string[];
        /**
         * @min -1
         * @example 1
         */
        offset?: number;
        /**
         * @min 0
         * @example 1
         */
        numberPeriodsBack?: number;
        /** @example 1 */
        periodId?: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetPositionRes, BaseErrorResponse>({
        path: `/api3/account/${id}/position`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account position
     * @name AccountPositionControllerGetLastBinaryPosition
     * @summary Getting a last binary position
     * @request GET:/api3/account/{id}/position/last-binary
     * @secure
     * @response `200` `GetLastBinaryPositionRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPositionControllerGetLastBinaryPosition = (
      id: number,
      query?: {
        treeId?: number;
        positionId?: number;
        properties?: string[];
      },
      params: RequestParams = {},
    ) =>
      this.request<GetLastBinaryPositionRes, BaseErrorResponse>({
        path: `/api3/account/${id}/position/last-binary`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account position
     * @name ChangeSponsorControllerChangeSponsor
     * @summary Changes the position sponsor
     * @request POST:/api3/account/{id}/position/{positionId}/change-sponsor
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    changeSponsorControllerChangeSponsor = (
      positionId: any,
      id: string,
      data: ChangeSponsorReq,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/position/${positionId}/change-sponsor`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
         * No description
         *
         * @tags Account position
         * @name ChangeSponsorControllerChangeFrontlineSponsor
         * @summary Changes the frontline's sponsor
         * @request POST:/api3/account/{id}/position/{positionId}/change-sponsor/frontline
         * @secure
         * @response `200` `EmptySuccessResponse`
         * @response `400` `BaseErrorResponse`
         */
    changeSponsorControllerChangeFrontlineSponsor = (
      positionId: any,
      id: string,
      data: ChangeSponsorFrontlineReq,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/position/${positionId}/change-sponsor/frontline`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ProfileControllerGetProfile
     * @summary Returns the list of values of the profile fields
     * @request GET:/api3/account/{id}/profile
     * @secure
     * @response `200` `GetProfileValuesRes`
     * @response `400` `BaseErrorResponse`
     */
    profileControllerGetProfile = (id: number, params: RequestParams = {}) =>
      this.request<GetProfileValuesRes, BaseErrorResponse>({
        path: `/api3/account/${id}/profile`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ProfileControllerSetProfile
     * @summary Sets the profile fields
     * @request PUT:/api3/account/{id}/profile
     * @secure
     * @response `200` `SetProfileReq`
     * @response `400` `BaseErrorResponse`
     */
    profileControllerSetProfile = (id: number, data: SetProfileReq, params: RequestParams = {}) =>
      this.request<SetProfileReq, BaseErrorResponse>({
        path: `/api3/account/${id}/profile`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ProfileControllerGetProfileField
     * @summary Returns the value of the field with the passed alias
     * @request GET:/api3/account/{id}/profile/{alias}
     * @secure
     * @response `200` `GetProfileValueRes`
     * @response `400` `BaseErrorResponse`
     */
    profileControllerGetProfileField = (alias: string, id: number, params: RequestParams = {}) =>
      this.request<GetProfileValueRes, BaseErrorResponse>({
        path: `/api3/account/${id}/profile/${alias}`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ProfileControllerSetProfileField
     * @summary Sets the value of the field with the passed alias
     * @request PUT:/api3/account/{id}/profile/{alias}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    profileControllerSetProfileField = (
      alias: string,
      id: number,
      data: SetProfileValueReq,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/profile/${alias}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account profile
     * @name ProfileControllerDeleteProfileField
     * @summary Removing the value of the profile field with the passed alias
     * @request DELETE:/api3/account/{id}/profile/{alias}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    profileControllerDeleteProfileField = (alias: string, id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/profile/${alias}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account properties
     * @name AccountPropertyControllerGetProperties
     * @summary Getting the account properties
     * @request GET:/api3/account/{id}/property
     * @secure
     * @response `200` `GetAccountPropertiesRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPropertyControllerGetProperties = (
      id: any,
      query?: {
        /**
         * @min 0
         * @example 0
         */
        positionOffset?: number;
        /** @example [] */
        properties?: string;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountPropertiesRes, BaseErrorResponse>({
        path: `/api3/account/${id}/property`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account properties
     * @name AccountPropertyControllerGetPropertiesHistory
     * @summary Getting the history of account properties
     * @request GET:/api3/account/{id}/property/history
     * @secure
     * @response `200` `GetAccountPropertiesHistoryRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPropertyControllerGetPropertiesHistory = (
      id: any,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 0 */
        positionOffset?: number;
        properties?: string[];
        /** @example true */
        includeCurrent?: boolean;
        /**
         * Sort options
         * @example {"orderBy":{"period.offset":"ASC"}}
         */
        orderBy?: object;
        /**
         * Filtering parameters
         * @example {"filter":[{"field":"period.created_at","value":"2023-01-01","operation":">"}]}
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountPropertiesHistoryRes, BaseErrorResponse>({
        path: `/api3/account/${id}/property/history`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account properties
     * @name AccountPropertyControllerGetProperty
     * @summary Getting the account property
     * @request GET:/api3/account/{id}/property/{propertyId}
     * @secure
     * @response `200` `GetAccountPropertyRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPropertyControllerGetProperty = (
      propertyId: number,
      id: any,
      query?: {
        /**
         * @min 0
         * @example 0
         */
        positionOffset?: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountPropertyRes, BaseErrorResponse>({
        path: `/api3/account/${id}/property/${propertyId}`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account properties
     * @name AccountPropertyControllerSetProperty
     * @summary Sets the account plan property
     * @request PUT:/api3/account/{id}/property/{propertyId}
     * @secure
     * @response `200` `SetAccountPropertyRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPropertyControllerSetProperty = (
      id: any,
      propertyId: any,
      data: EditPropertyReq,
      query?: {
        /** @example true */
        directEdit?: boolean;
        /** @example 1 */
        offset?: any;
      },
      params: RequestParams = {},
    ) =>
      this.request<SetAccountPropertyRes, BaseErrorResponse>({
        path: `/api3/account/${id}/property/${propertyId}`,
        method: 'PUT',
        query,
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account properties
     * @name AccountPropertyControllerGetPropertyHistory
     * @summary Getting the history of the account property
     * @request GET:/api3/account/{id}/property/{propertyId}/history
     * @secure
     * @response `200` `GetAccountPropertiesHistoryRes`
     * @response `400` `BaseErrorResponse`
     */
    accountPropertyControllerGetPropertyHistory = (
      propertyId: number,
      id: any,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 0 */
        positionOffset?: number;
        /** @example false */
        includeCurrent?: boolean;
        /**
         * Sort options
         * @example {"orderBy":{"period.offset":"ASC"}}
         */
        orderBy?: object;
        /**
         * Filtering parameters
         * @example {"filter":[{"field":"period.created_at","value":"2023-01-01","operation":">"}]}
         */
        filter?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetAccountPropertiesHistoryRes, BaseErrorResponse>({
        path: `/api3/account/${id}/property/${propertyId}/history`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account search
     * @name SearchControllerGetAccountByInviteCode
     * @summary Returns the account by invite code
     * @request GET:/api3/account/search/by-invite-code
     * @response `200` `SearchRes`
     * @response `400` `BaseErrorResponse`
     */
    searchControllerGetAccountByInviteCode = (
      query: {
        /**
         * @minLength 4
         * @maxLength 100
         * @example ""
         */
        inviteCode: string;
      },
      params: RequestParams = {},
    ) =>
      this.request<SearchRes, BaseErrorResponse>({
        path: '/api3/account/search/by-invite-code',
        method: 'GET',
        query,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account search
     * @name SearchControllerGetAccountByLogin
     * @summary Returns the account by login
     * @request GET:/api3/account/search/by-login
     * @secure
     * @response `200` `SearchRes`
     * @response `400` `BaseErrorResponse`
     */
    searchControllerGetAccountByLogin = (
      query: {
        /**
         * @minLength 2
         * @maxLength 100
         * @example "user1@example.com"
         */
        login: string;
      },
      params: RequestParams = {},
    ) =>
      this.request<SearchRes, BaseErrorResponse>({
        path: '/api3/account/search/by-login',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account search
     * @name SearchControllerGetAccountById
     * @summary Returns the account by id
     * @request GET:/api3/account/search/by-id
     * @response `200` `SearchRes`
     * @response `400` `BaseErrorResponse`
     */
    searchControllerGetAccountById = (
      query: {
        /** @example "123" */
        id: number;
        /** @example "true" */
        showInactive?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<SearchRes, BaseErrorResponse>({
        path: '/api3/account/search/by-id',
        method: 'GET',
        query,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account search
     * @name SearchControllerGetAccountByExternalId
     * @summary Returns the account by external id
     * @request GET:/api3/account/search/by-external-id
     * @response `200` `SearchRes`
     * @response `400` `BaseErrorResponse`
     */
    searchControllerGetAccountByExternalId = (
      query: {
        /** @example "123" */
        externalId: string;
        /** @example "true" */
        showInactive?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<SearchRes, BaseErrorResponse>({
        path: '/api3/account/search/by-external-id',
        method: 'GET',
        query,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account search
     * @name SearchControllerSearchAccount
     * @summary Performs an account search
     * @request GET:/api3/account/search
     * @secure
     * @response `200` `SearchArrayRes`
     * @response `400` `BaseErrorResponse`
     */
    searchControllerSearchAccount = (
      query: {
        /**
         * @minLength 1
         * @maxLength 100
         * @example "Test test"
         */
        searchText: string;
        /** @example false */
        networkSearch?: boolean;
        /** @example ["firstname","lastname"] */
        profileFields?: string[];
        showDisabledAccounts?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<SearchArrayRes, BaseErrorResponse>({
        path: '/api3/account/search',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account send
     * @name SendControllerSendSupportMail
     * @summary Send support mail
     * @request POST:/api3/account/send/support-mail
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    sendControllerSendSupportMail = (data: SendSupportMailReq, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: '/api3/account/send/support-mail',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * @description Self-registration, which is used on the public page onlineoffice. Since there is no authorization in the API at this stage, the function only works with OTP.
     *
     * @tags Account creation
     * @name AccountSignupControllerCreateAccount
     * @summary Account registration
     * @request POST:/api3/account/signup
     * @response `200` `AccountSignupRes`
     * @response `400` `BaseErrorResponse`
     */
    accountSignupControllerCreateAccount = (data: AccountSignupReq, params: RequestParams = {}) =>
      this.request<AccountSignupRes, BaseErrorResponse>({
        path: '/api3/account/signup',
        method: 'POST',
        body: data,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account transaction log
     * @name AccountTransactionLogControllerGetAccountTransactionLog
     * @summary Getting the account transaction log
     * @request GET:/api3/account/{id}/transaction-log
     * @secure
     * @response `200` `GetTransactionLogRes`
     * @response `400` `BaseErrorResponse`
     */
    accountTransactionLogControllerGetAccountTransactionLog = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 1 */
        docId?: any;
        /** @example "Purchase" */
        documentType?: string;
        /** @example 1 */
        periodId?: any;
        /** @example "PV" */
        planProperty?: string;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetTransactionLogRes, BaseErrorResponse>({
        path: `/api3/account/${id}/transaction-log`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * @description Please use the /account/:id/property/:propertyId method
     *
     * @tags Account trees
     * @name AccountTreeControllerSetAccountProperty
     * @summary Sets the account plan property
     * @request PUT:/api3/account/{id}/tree/{treeId}/property/{propertyId}
     * @deprecated
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    accountTreeControllerSetAccountProperty = (
      id: number,
      treeId: number,
      propertyId: number,
      data: EditPropertyReq,
      query?: {
        /** @example true */
        directEdit?: boolean;
        /** @example 1 */
        offset?: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/tree/${treeId}/property/${propertyId}`,
        method: 'PUT',
        query,
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name UniquenessControllerOtpUniquenessCheck
     * @summary Checking the uniqueness of email and phone
     * @request POST:/api3/account/uniqueness-check
     * @response `200` `UniquenessCheckRes`
     * @response `400` `BaseErrorResponse`
     */
    uniquenessControllerOtpUniquenessCheck = (data: UniquenessCheckReq, params: RequestParams = {}) =>
      this.request<UniquenessCheckRes, BaseErrorResponse>({
        path: '/api3/account/uniqueness-check',
        method: 'POST',
        body: data,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Account
     * @name UplineControllerGetUpline
     * @summary Getting the upline of the position
     * @request GET:/api3/account/{id}/upline
     * @secure
     * @response `200` `GetUplineRes`
     * @response `400` `BaseErrorResponse`
     */
    uplineControllerGetUpline = (
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example 0 */
        treeId?: any;
        /** @example null */
        positionId?: any;
        /**
         * @min 1
         * @example 1
         */
        offset?: any;
        /**
         * target position to which the upline is requested
         * @min 1
         * @example 1
         */
        targetPositionId?: any;
        /**
         * Requested marketing properties
         * @example ["PV","AR"]
         */
        properties?: string;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetUplineRes, BaseErrorResponse>({
        path: `/api3/account/${id}/upline`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet
     * @name WalletOperationControllerGetOperations
     * @summary Getting wallet operations
     * @request GET:/api3/account/{id}/wallet/{walletId}/operation
     * @secure
     * @response `200` `GetTransactionsRes`
     * @response `400` `BaseErrorResponse`
     */
    walletOperationControllerGetOperations = (
      walletId: number,
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /**
         * Operation type ID
         * @example 1
         */
        operationTypeId?: number;
        /**
         * Operation type IDs
         * @example [1,2]
         */
        operationTypeIds?: string[];
        /**
         * Payment system ID
         * @example 1
         */
        paymentSystemId?: number;
        /**
         * Payment rule ID
         * @example 1
         */
        paymentRuleId?: number;
        /**
         * Currency
         * @example "USD"
         */
        currencyId?: string;
        /**
         * Date
         * @example "2021-10-01"
         */
        start?: any;
        /**
         * Date
         * @example "2021-10-01"
         */
        end?: any;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetTransactionsRes, BaseErrorResponse>({
        path: `/api3/account/${id}/wallet/${walletId}/operation`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet
     * @name WalletOperationControllerCreateOperations
     * @summary Creating a wallet operation
     * @request POST:/api3/account/{id}/wallet/{walletId}/operation
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    walletOperationControllerCreateOperations = (
      walletId: number,
      id: number,
      data: PostTransactionReq,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/wallet/${walletId}/operation`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet
     * @name WalletOperationControllerExportOperations
     * @summary Getting wallet operations
     * @request POST:/api3/account/{id}/wallet/{walletId}/operation/export
     * @secure
     * @response `200` `GetTransactionsRes`
     * @response `400` `BaseErrorResponse`
     */
    walletOperationControllerExportOperations = (
      walletId: number,
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /**
         * Operation type ID
         * @example 1
         */
        operationTypeId?: number;
        /**
         * Operation type IDs
         * @example [1,2]
         */
        operationTypeIds?: string[];
        /**
         * Payment system ID
         * @example 1
         */
        paymentSystemId?: number;
        /**
         * Payment rule ID
         * @example 1
         */
        paymentRuleId?: number;
        /**
         * Currency
         * @example "USD"
         */
        currencyId?: string;
        /**
         * Date
         * @example "2021-10-01"
         */
        start?: any;
        /**
         * Date
         * @example "2021-10-01"
         */
        end?: any;
      },
      data: ExportOperationParams,
      params: RequestParams = {},
    ) =>
      this.request<GetTransactionsRes, BaseErrorResponse>({
        path: `/api3/account/${id}/wallet/${walletId}/operation/export`,
        method: 'POST',
        query,
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet
     * @name WalletTransferControllerCreateTransfer
     * @request POST:/api3/account/{id}/wallet/{walletId}/external-transfer
     * @secure
     * @response `201` `void`
     */
    walletTransferControllerCreateTransfer = (
      walletId: number,
      id: number,
      data: PostTransferReq,
      params: RequestParams = {},
    ) =>
      this.request<unknown, any>({
        path: `/api3/account/${id}/wallet/${walletId}/external-transfer`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet
     * @name WalletTransferControllerCreateInternalTransfer
     * @request POST:/api3/account/{id}/wallet/{walletId}/internal-transfer
     * @secure
     * @response `201` `void`
     */
    walletTransferControllerCreateInternalTransfer = (
      walletId: number,
      id: number,
      data: InternalTransferReq,
      params: RequestParams = {},
    ) =>
      this.request<unknown, any>({
        path: `/api3/account/${id}/wallet/${walletId}/internal-transfer`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet
     * @name WalletAliasControllerGetWalletsList
     * @summary Get a list of account wallets
     * @request GET:/api3/account/{id}/wallet
     * @secure
     * @response `200` `GetWalletsListRes`
     * @response `400` `BaseErrorResponse`
     */
    walletAliasControllerGetWalletsList = (
      id: number,
      query?: {
        /** @example "2021-10-01" */
        date?: any;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetWalletsListRes, BaseErrorResponse>({
        path: `/api3/account/${id}/wallet`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * @description Please use the account/id/wallet/:walletId/operation methods
     *
     * @tags Wallet
     * @name WalletAliasControllerGetTransaction
     * @summary Getting wallets operations
     * @request GET:/api3/account/{id}/wallet/{walletAlias}/transaction
     * @deprecated
     * @secure
     * @response `200` `GetTransactionRes`
     * @response `400` `BaseErrorResponse`
     */
    walletAliasControllerGetTransaction = (
      walletAlias: string,
      id: number,
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /**
         * Operation type ID
         * @example 1
         */
        operationTypeId?: number;
        /**
         * Date
         * @example "2021-10-01"
         */
        start?: any;
        /**
         * Date
         * @example "2021-10-01"
         */
        end?: any;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetTransactionRes, BaseErrorResponse>({
        path: `/api3/account/${id}/wallet/${walletAlias}/transaction`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * @description Please use the account/{id}/wallet/{walletId}/operation methods
     *
     * @tags Wallet
     * @name WalletAliasControllerCreateTransaction
     * @summary Creating a wallet transaction
     * @request POST:/api3/account/{id}/wallet/{walletAlias}/transaction
     * @deprecated
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    walletAliasControllerCreateTransaction = (
      walletAlias: string,
      id: number,
      data: PostTransactionReq,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/wallet/${walletAlias}/transaction`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * @description Please use the account/{id}/wallet/{walletId}/transfer methods
     *
     * @tags Wallet
     * @name WalletAliasControllerCreateTransfer
     * @summary Transferring money from a specified wallet to another
     * @request POST:/api3/account/{id}/wallet/{walletAlias}/transfer
     * @deprecated
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    walletAliasControllerCreateTransfer = (
      walletAlias: string,
      id: number,
      data: PostTransferReq,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/account/${id}/wallet/${walletAlias}/transfer`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });
  }

  return new Account();
}
